import { Controller } from "@hotwired/stimulus"
import { showToast } from "../utils/show_toast"
import { patchImageAltText, patchInlineImageText } from "../utils/patch_image_alt_text"
import { csrfToken } from "../utils/csrf_token"

const ALLOWED_IMAGE_TYPES = new Set(["image/webp", "image/jpeg", "image/png"])
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB

export default class extends Controller {
  static targets = ["input", "dropzone", "preview", "uploads", "cropModal", "galleryTableShell", "defaultPreview", "purgeImageFlag"]
  static values = {
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 11 },
    tierLimit: { type: Number, default: 0 },
    uploadUrl: { type: String, default: "" },
    hiddenNoteMessage: { type: String, default: "" },
    // Category custom icon: reject non-WebP at pick/drop; crop still posts PNG to the server.
    webpOnly: { type: Boolean, default: false },
    // When true, cropped file stays client-side only (DataTransfer on file input); no POST to uploadUrl.
    localOnly: { type: Boolean, default: false },
    altSavedEvent: { type: String, default: "" },
    captionSavedEvent: { type: String, default: "" },
    deletedEvent: { type: String, default: "" }
  }

  static processingDelayMs = 10_000
  static pollIntervalMs = 1_000
  static pollMaxMs = 30_000

  connect() {
    this.pendingFiles = []
    this.localObjectUrls = []
    this.nextFileId = 0
    this.uploadInProgress = false
    this.processingTimeouts = []
    this.processingIntervals = []

    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)
    this.boundClick = this.click.bind(this)

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.addEventListener("dragover", this.boundDragOver)
      this.dropzoneTarget.addEventListener("dragleave", this.boundDragLeave)
      this.dropzoneTarget.addEventListener("drop", this.boundDrop)
      this.dropzoneTarget.addEventListener("click", this.boundClick)
    }

    if (this.hasInputTarget) {
      // Start disabled so an empty file input is excluded from form params.
      this.inputTarget.disabled = true
      this.boundInputChange = this.inputChange.bind(this)
      this.inputTarget.addEventListener("change", this.boundInputChange)
    }

    this.boundRemoveClick = this.handleRemoveClick.bind(this)
    this.boundAltSaveClick = this.handleAltSaveClick.bind(this)
    this.boundDetailSaveClick = this.handleDetailSaveClick.bind(this)
    this.boundAltInputKeydown = this.handleDetailInputKeydown.bind(this)
    this.boundCropComplete = this.onCropComplete.bind(this)
    this.element.addEventListener("click", this.boundRemoveClick)
    this.element.addEventListener("click", this.boundAltSaveClick)
    this.element.addEventListener("click", this.boundDetailSaveClick)
    this.element.addEventListener("keydown", this.boundAltInputKeydown)
    this.element.addEventListener("image-crop:complete", this.boundCropComplete)

    this.updateMainImageDropzoneVisibility()
    this.syncGalleryTableShell()
    this.syncHiddenByPlanNote()
  }

  disconnect() {
    this.processingTimeouts.forEach((id) => clearTimeout(id))
    this.processingTimeouts = []
    this.processingIntervals.forEach((id) => clearInterval(id))
    this.processingIntervals = []
    if (this.clickReenableTimeout) {
      clearTimeout(this.clickReenableTimeout)
      this.clickReenableTimeout = null
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener("dragover", this.boundDragOver)
      this.dropzoneTarget.removeEventListener("dragleave", this.boundDragLeave)
      this.dropzoneTarget.removeEventListener("drop", this.boundDrop)
      this.dropzoneTarget.removeEventListener("click", this.boundClick)
    }
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("change", this.boundInputChange)
    }
    this.element.removeEventListener("click", this.boundRemoveClick)
    this.element.removeEventListener("click", this.boundAltSaveClick)
    this.element.removeEventListener("click", this.boundDetailSaveClick)
    this.element.removeEventListener("keydown", this.boundAltInputKeydown)
    this.element.removeEventListener("image-crop:complete", this.boundCropComplete)

    if (this.localObjectUrls?.length) {
      this.localObjectUrls.forEach((u) => URL.revokeObjectURL(u))
      this.localObjectUrls = []
    }
  }

  onCropComplete(event) {
    const file = event.detail?.file
    if (file) this.processFiles([file])
  }

  handleRemoveClick(event) {
    if (!event.target.classList.contains("remove-upload-btn")) return

    event.preventDefault()
    if (!window.confirm("Are you sure you want to delete this image?")) return

    const wrapper = event.target.closest("[data-file-id], [data-signed-id]")
    this.removeUploadByElement(wrapper)
  }

  handleDetailSaveClick(event) {
    const btn = event.target.closest("[data-image-detail-save]")
    if (!btn) return

    event.preventDefault()
    this.commitInlineImageSave(btn)
  }

  handleDetailInputKeydown(event) {
    if (event.key !== "Enter" || event.isComposing) return
    const input = event.target.closest("[data-image-alt-input], [data-image-caption-input]")
    if (!input || !this.element.contains(input)) return

    const combined = input.closest("[data-image-upload-detail]")?.querySelector("[data-image-detail-save]")
    if (combined) {
      event.preventDefault()
      this.commitInlineImageSave(combined)
      return
    }

    this.handleAltInputKeydown(event)
  }

  commitInlineImageSave(btn) {
    const wrapper = btn.closest("[data-signed-id], [data-file-id]")
    if (!wrapper) return

    const altInput = wrapper.querySelector("[data-image-alt-input]")
    const captionInput = wrapper.querySelector("[data-image-caption-input]")
    const url = altInput?.dataset?.updateUrl
    if (!url || !altInput || !captionInput) return

    const alt = String(altInput.value ?? "").trim()
    const caption = String(captionInput.value ?? "").trim()
    const altUnchanged = alt === String(altInput.defaultValue ?? "").trim()
    const captionUnchanged = caption === String(captionInput.defaultValue ?? "").trim()
    if (altUnchanged && captionUnchanged) return

    this.runDetailSave({
      btn,
      request: () => patchInlineImageText(url, altInput.value, captionInput.value),
      successMessage: "Saved",
      failureMessage: "Failed to save",
      onSuccess: () => {
        altInput.defaultValue = alt
        captionInput.defaultValue = caption
        const signedId = wrapper.dataset.blobSignedId
        if (!signedId) return

        this.dispatchNamedEvent(this.altSavedEventValue, { signedId, alt })
        this.dispatchNamedEvent(this.captionSavedEventValue, { signedId, caption })
      }
    })
  }

  handleAltSaveClick(event) {
    const btn = event.target.closest("[data-image-alt-save]")
    if (!btn) return

    event.preventDefault()
    this.commitAltTextSave(btn)
  }

  // Bound via data-action on Save so the click is handled before outer controllers (e.g. lightbox).
  saveAltFromButton(event) {
    event.preventDefault()
    event.stopPropagation()
    this.commitAltTextSave(event.currentTarget)
  }

  commitAltTextSave(btn) {
    const field = this.detailField(btn, "[data-image-alt-input]")
    if (!field) return

    const trimmed = String(field.input.value ?? "").trim()
    if (trimmed === String(field.input.defaultValue ?? "").trim()) return

    this.runDetailSave({
      btn: field.btn,
      request: () => patchImageAltText(field.url, field.input.value),
      successMessage: "Alt text saved",
      failureMessage: "Failed to save alt text",
      onSuccess: () => {
        field.input.defaultValue = trimmed
        const signedId = field.input.closest("[data-blob-signed-id]")?.dataset?.blobSignedId
        if (!signedId) return

        this.dispatchNamedEvent(this.altSavedEventValue, { signedId, alt: trimmed })
      }
    })
  }

  handleAltInputKeydown(event) {
    this.saveDetailOnEnter(event, "[data-image-alt-input]", "[data-image-alt-save]", (btn) => {
      this.commitAltTextSave(btn)
    })
  }

  detailField(btn, inputSelector) {
    if (!btn) return null
    const wrapper = btn.closest("[data-signed-id], [data-file-id]")
    if (!wrapper) return null

    const input = wrapper.querySelector(inputSelector) || btn.previousElementSibling
    const url = input?.dataset?.updateUrl
    if (!url || !input) return null

    return { url, input, btn }
  }

  saveDetailOnEnter(event, inputSelector, saveSelector, commit) {
    if (event.key !== "Enter" || event.isComposing) return
    const input = event.target.closest(inputSelector)
    if (!input || !this.element.contains(input)) return
    if (!input.dataset.updateUrl) return

    const btn =
      input.parentElement?.querySelector(saveSelector) ||
      input.closest("[data-image-upload-detail]")?.querySelector(saveSelector)
    if (!btn) return

    event.preventDefault()
    commit(btn)
  }

  runDetailSave({ btn, request, successMessage, failureMessage, onSuccess }) {
    const prevIcon = btn.querySelector("i")?.className
    if (prevIcon) btn.querySelector("i").className = "fa-solid fa-spinner animate-spin text-[0.85rem] leading-none"

    request()
      .then((response) => {
        if (!response.ok) {
          showToast(failureMessage, "error")
          return
        }

        showToast(successMessage)
        onSuccess?.()
      })
      .catch(() => {
        showToast(failureMessage, "error")
      })
      .finally(() => {
        if (prevIcon && btn.querySelector("i")) {
          btn.querySelector("i").className = prevIcon
        }
      })
  }

  dispatchNamedEvent(name, detail) {
    if (!name) return

    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }))
  }

  previewImageElement() {
    if (!this.hasPreviewTarget) return null
    return this.previewTarget.querySelector("img[src]")
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.uploadInProgress) return
    this.dropzoneTarget.classList.add("ring-2", "ring-[var(--brand-green)]", "ring-offset-2")
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("ring-2", "ring-[var(--brand-green)]", "ring-offset-2")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("ring-2", "ring-[var(--brand-green)]", "ring-offset-2")
    if (this.uploadInProgress) return

    const result = this.validateFiles(Array.from(event.dataTransfer.files))
    if (result.invalidType.length > 0) {
      showToast(this.invalidTypeToastMessage(result.invalidType.length), "error")
    }
    if (result.invalidSize.length > 0) {
      showToast(`${result.invalidSize.length} file(s) skipped — max 5 MB each`, "error", { duration: 4000 })
    }
    if (result.valid.length > 0) {
      this.openCropOrProcess(result.valid)
    }
  }

  click(event) {
    if (this.uploadInProgress) return
    if (event.target === this.dropzoneTarget || this.dropzoneTarget.contains(event.target)) {
      // Disabled inputs ignore programmatic click() — temporarily enable so the file picker opens
      this.inputTarget.disabled = false
      this.inputTarget.click()
      // Re-disable if user cancelled (no change event). Delay allows change to fire first if they selected.
      if (this.clickReenableTimeout) clearTimeout(this.clickReenableTimeout)
      this.clickReenableTimeout = setTimeout(() => {
        this.clickReenableTimeout = null
        if (this.hasInputTarget && this.inputTarget.files.length === 0 && this.pendingFiles.length === 0) {
          this.inputTarget.disabled = true
        }
      }, 500)
    }
  }

  inputChange(event) {
    const allFiles = Array.from(event.target.files || [])
    event.target.value = ""
    if (allFiles.length === 0) return

    const result = this.validateFiles(allFiles)
    if (result.invalidType.length > 0) {
      showToast(this.invalidTypeToastMessage(result.invalidType.length), "error")
    }
    if (result.invalidSize.length > 0) {
      showToast(`${result.invalidSize.length} file(s) skipped — max 5 MB each`, "error", { duration: 4000 })
    }
    if (result.valid.length > 0) {
      this.openCropOrProcess(result.valid)
    }
  }

  openCropOrProcess(files) {
    if (this.hasCropModalTarget && files.length > 0) {
      this.cropModalTarget.dispatchEvent(new CustomEvent("image-crop:open", { detail: { file: files[0] }, bubbles: false }))
    } else if (files.length > 0) {
      this.processFiles(files)
    }
  }

  processFiles(files) {
    let toAdd = files

    if (this.multipleValue) {
      const existingCount = this.hasUploadsTarget
        ? this.uploadsTarget.querySelectorAll("[data-signed-id]").length
        : 0
      const currentCount = this.pendingFiles.length + existingCount
      const maxFiles = this.tierLimitValue > 0
        ? this.tierLimitValue
        : this.maxFilesValue
      const remaining = Math.max(0, maxFiles - currentCount)
      toAdd = files.slice(0, Math.min(1, remaining))
      if (files.length > 1) {
        showToast("You can only add one image at a time. Add more by selecting or dropping again.", "info")
      }
    } else {
      toAdd = [files[0]]
      this.pendingFiles = []
    }

    toAdd.forEach((file) => {
      const id = this.nextFileId++
      this.pendingFiles.push({ id, file })
      this.showPreview(file, id)
    })

    this.syncFilesToInput()
    // localOnly: finishLocalPendingUpload runs from showPreview FileReader.onload (avoids racing
    // the async reader — sync finish left the processing overlay stuck on the thumb).
    if (!this.localOnlyValue) {
      this.uploadWithFiles()
    }
  }

  finishLocalPendingUpload() {
    if (!this.localOnlyValue || !this.hasPreviewTarget || this.multipleValue) return
    const wrapper = this.previewTarget.querySelector("[data-file-id]")
    if (!wrapper || !this.pendingFiles.length) return

    const file = this.pendingFiles[0].file
    const url = URL.createObjectURL(file)
    this.localObjectUrls.push(url)
    wrapper.dataset.fullSrc = url

    this.clearProcessingState(wrapper)

    this.element.dispatchEvent(new CustomEvent("image-ready", { bubbles: true, detail: { url } }))
  }

  // The input stays disabled when empty so it is excluded from form params
  syncFilesToInput() {
    if (!this.hasInputTarget) return
    const dt = new DataTransfer()
    this.pendingFiles.forEach(({ file }) => dt.items.add(file))
    this.inputTarget.files = dt.files
    this.inputTarget.disabled = this.pendingFiles.length === 0
  }

  thumbShellFor(wrapper) {
    if (!wrapper) return null
    return wrapper.querySelector(".image-upload-thumb-shell") || wrapper
  }

  showPreview(file, id) {
    if (!this.multipleValue && this.hasPreviewTarget) {
      this.previewTarget.replaceChildren()
    }

    const imgClass = "h-32 w-auto rounded border border-gray-200 object-contain"
    const placeholderClass = "h-32 w-32 rounded border border-gray-200"

    const thumbShellClass =
      "relative shrink-0 self-start sm:self-start cursor-pointer image-upload-thumb-shell justify-self-start"

    let wrapper
    let detailCol
    let thumbShell

    let appendRoot = null

    if (this.multipleValue && this.hasUploadsTarget) {
      wrapper = document.createElement("div")
      wrapper.className =
        "overflow-x-auto rounded-lg border-2 border-(--brand-gray) bg-white shadow-md"
      wrapper.dataset.fileId = id

      const inner = document.createElement("div")
      inner.className = this.previewRowClassName()

      if (this.gallerySortEnabled()) {
        inner.appendChild(this.createGalleryDragHandle())
      }

      thumbShell = document.createElement("div")
      thumbShell.className = thumbShellClass
      inner.appendChild(thumbShell)

      detailCol = document.createElement("div")
      detailCol.className = "min-w-0 w-full"
      detailCol.setAttribute("data-image-upload-detail", "")
      inner.appendChild(detailCol)

      wrapper.appendChild(inner)
    } else if (!this.multipleValue && this.hasPreviewTarget) {
      const shell = document.createElement("div")
      shell.className =
        "overflow-x-auto rounded-lg border-2 border-(--brand-gray) bg-white shadow-md"

      wrapper = document.createElement("div")
      wrapper.className =
        "grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-4 px-4 py-4 sm:items-start"
      wrapper.dataset.fileId = id

      thumbShell = document.createElement("div")
      thumbShell.className = thumbShellClass
      wrapper.appendChild(thumbShell)

      detailCol = document.createElement("div")
      detailCol.className = "min-w-0 w-full"
      detailCol.setAttribute("data-image-upload-detail", "")
      wrapper.appendChild(detailCol)

      shell.appendChild(wrapper)
      appendRoot = shell
    } else {
      wrapper = document.createElement("div")
      wrapper.className = this.multipleValue
        ? this.previewRowClassName()
        : `${this.previewRowClassName()} rounded-lg border border-gray-200 bg-white`
      wrapper.dataset.fileId = id

      thumbShell = document.createElement("div")
      thumbShell.className = thumbShellClass
      wrapper.appendChild(thumbShell)

      detailCol = document.createElement("div")
      detailCol.className = "min-w-0 w-full"
      detailCol.setAttribute("data-image-upload-detail", "")
      wrapper.appendChild(detailCol)
    }

    const idText = document.createElement("p")
    idText.className = "text-[11px] font-semibold text-gray-400 truncate"
    idText.dataset.role = "blob-id"
    detailCol.appendChild(idText)

    const metaText = document.createElement("p")
    metaText.className = "text-[11px] text-gray-400 truncate mb-0.5"
    metaText.textContent = file.name
    detailCol.appendChild(metaText)

    const placeholderEl = document.createElement("div")
    placeholderEl.className = `${placeholderClass} bg-gray-100 flex flex-col items-center justify-center gap-1 text-gray-400`
    const spinnerContainer = document.createRange().createContextualFragment(this.spinnerSvg("w-5 h-5"))
    placeholderEl.appendChild(spinnerContainer)
    const nameSpan = document.createElement("span")
    nameSpan.className = "text-xs truncate max-w-[5rem] px-1"
    nameSpan.textContent = file.name
    placeholderEl.appendChild(nameSpan)
    thumbShell.appendChild(placeholderEl)

    const processingOverlayHtml = `
      <div class="image-processing-overlay absolute inset-0 flex items-center justify-center bg-black/40 rounded pointer-events-none">
        ${this.spinnerSvg("w-6 h-6 text-white")}
      </div>
    `
    const reader = new FileReader()
    reader.onload = (e) => {
      thumbShell.replaceChildren()
      const img = document.createElement("img")
      img.src = e.target.result
      img.className = imgClass
      img.alt = "Upload preview"
      thumbShell.appendChild(img)
      if (this.localOnlyValue && !this.multipleValue) {
        this.finishLocalPendingUpload()
      } else {
        thumbShell.appendChild(document.createRange().createContextualFragment(processingOverlayHtml))
      }
    }
    reader.readAsDataURL(file)

    const container = this.hasUploadsTarget
      ? this.uploadsTarget
      : this.hasPreviewTarget
        ? this.previewTarget
        : this.dropzoneTarget
    if (appendRoot) {
      container.appendChild(appendRoot)
    } else {
      container.appendChild(wrapper)
    }
    this.updateMainImageDropzoneVisibility()
    if (this.multipleValue) {
      this.dispatchGalleryCountChanged()
      this.syncGalleryTableShell()
    }
  }

  removeUploadByElement(wrapper) {
    if (!wrapper) return
    if (this.isProcessing(wrapper)) {
      showToast("Please wait, image is still processing", "error")
      return
    }

    if (wrapper.dataset.signedId && !wrapper.hasAttribute("data-file-id")) {
      if (this.hasPurgeImageFlagTarget) this.purgeImageFlagTarget.value = "1"
    }

    const fullSrc = wrapper.dataset.fullSrc
    if (fullSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(fullSrc)
      if (this.localObjectUrls) {
        this.localObjectUrls = this.localObjectUrls.filter((u) => u !== fullSrc)
      }
    }

    const fileId = wrapper.dataset.fileId
    const deleteUrl = wrapper.dataset.deleteUrl
    const signedId = wrapper.dataset.blobSignedId
    if (signedId) {
      this.dispatchNamedEvent(this.deletedEventValue, { signedId })
    }

    if (fileId !== undefined) {
      this.pendingFiles = this.pendingFiles.filter(({ id }) => String(id) !== String(fileId))
      this.syncFilesToInput()
    }

    const localSingle = this.localOnlyValue && !this.multipleValue

    const elementToRemove = this.elementToRemoveFor(wrapper)
    elementToRemove.remove()

    if (localSingle) {
      this.revealDropzone()
    } else {
      this.updateMainImageDropzoneVisibility()
    }
    this.syncHiddenByPlanNote()
    if (this.multipleValue) {
      this.dispatchGalleryCountChanged()
      this.syncGalleryTableShell()
    }
    if (deleteUrl) {
      this.deleteImage(deleteUrl)
    }
    if (
      this.localOnlyValue &&
      this.hasPreviewTarget &&
      !this.previewTarget.querySelector("[data-file-id], [data-signed-id]")
    ) {
      this.element.dispatchEvent(new CustomEvent("image-cleared", { bubbles: true }))
    }
  }

  revealDropzone() {
    if (!this.hasDropzoneTarget) return

    this.dropzoneTarget.classList.remove("hidden")
  }

  deleteImage(url) {
    const preventUnload = (e) => { e.preventDefault(); e.returnValue = "" }
    window.addEventListener("beforeunload", preventUnload)
    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" }
    }).then((response) => {
      if (response.ok) {
        showToast("Image deleted")
      } else {
        showToast("Failed to delete image", "error")
      }
    }).catch(() => {
      showToast("Failed to delete image", "error")
    }).finally(() => {
      window.removeEventListener("beforeunload", preventUnload)
    })
  }

  validateFiles(files) {
    const valid = []
    const invalidType = []
    const invalidSize = []
    files.forEach((file) => {
      if (!this.isAllowedPickType(file.type)) {
        invalidType.push(file)
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        invalidSize.push(file)
      } else {
        valid.push(file)
      }
    })
    return { valid, invalidType, invalidSize }
  }

  isAllowedPickType(contentType) {
    if (this.webpOnlyValue) return contentType === "image/webp"
    return ALLOWED_IMAGE_TYPES.has(contentType)
  }

  invalidTypeToastMessage(count) {
    const suffix = this.webpOnlyValue ? "WebP only" : "WebP, JPEG, JPG, or PNG only"
    return `${count} file(s) skipped — ${suffix}`
  }

  spinnerSvg(classes = "w-6 h-6") {
    return `<svg class="${classes} animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
  }

  dispatchGalleryCountChanged() {
    this.element.dispatchEvent(new CustomEvent("gallery-images:count-changed", { bubbles: true }))
  }

  uploadWithFiles() {
    if (!this.uploadUrlValue || this.pendingFiles.length === 0) return

    const file = this.pendingFiles[0].file

    this.uploadInProgress = true
    this.setDropzoneDisabled(true)

    const formData = new FormData()
    formData.append("file", file)

    const preventUnload = (e) => { e.preventDefault(); e.returnValue = "" }
    window.addEventListener("beforeunload", preventUnload)
    fetch(this.uploadUrlValue, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest", "Accept": "application/json" }
    }).then((response) => {
      if (response.ok) {
        return response.json().then((data) => {
          this.applySingleUploadResponse(data)
          showToast("Image attached")
        })
      }
      this.removeFailedUploadPreviews(1)
      showToast("Failed to attach image", "error")
    }).catch(() => {
      this.removeFailedUploadPreviews(1)
      showToast("Failed to attach image", "error")
    }).finally(() => {
      window.removeEventListener("beforeunload", preventUnload)
      this.pendingFiles = []
      this.syncFilesToInput()
      this.uploadInProgress = false
      this.setDropzoneDisabled(false)
    })
  }

  isProcessing(wrapper) {
    const until = wrapper?.dataset?.processingUntil
    return until && Date.now() < parseInt(until, 10)
  }

  setProcessingState(wrapper, enabled, variantReadyUrl) {
    if (!wrapper) return
    if (enabled) {
      wrapper.classList.add("image-processing")
      wrapper.dataset.processingUntil = String(Date.now() + this.constructor.pollMaxMs)

      if (variantReadyUrl) {
        this.pollVariantReady(wrapper, variantReadyUrl)
      } else {
        const id = setTimeout(() => this.clearProcessingState(wrapper), this.constructor.processingDelayMs)
        this.processingTimeouts.push(id)
      }
    } else {
      this.clearProcessingState(wrapper)
    }
  }

  clearProcessingState(wrapper) {
    if (!wrapper) return
    const surface = this.thumbShellFor(wrapper)
    wrapper.classList.remove("image-processing")
    delete wrapper.dataset.processingUntil
    surface?.querySelector(".image-processing-overlay")?.remove()
    const thumbUrl = wrapper.dataset.thumbnailSrc
    if (thumbUrl && surface) {
      const img = surface.querySelector("img")
      if (img) img.src = thumbUrl
    }
    if (surface && !surface.querySelector(".remove-upload-btn")) {
      surface.appendChild(this.createRemoveButton())
    }
  }

  createRemoveButton() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "absolute top-1 -right-2 translate-x-[3px] w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center border border-red-700 hover:bg-red-600 cursor-pointer remove-upload-btn"
    btn.title = "Remove"
    btn.textContent = "×"
    return btn
  }

  pollVariantReady(wrapper, url) {
    const start = Date.now()
    let intervalId = null

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
        this.processingIntervals = this.processingIntervals.filter((i) => i !== intervalId)
        intervalId = null
      }
    }

    const poll = () => {
      if (!wrapper.isConnected) {
        stop()
        return
      }
      if (Date.now() - start > this.constructor.pollMaxMs) {
        this.clearProcessingState(wrapper)
        stop()
        return
      }

      fetch(url, { headers: { "Accept": "application/json" } })
        .then((r) => r.json())
        .then((data) => {
          if (data.ready) {
            this.clearProcessingState(wrapper)
            stop()
          }
        })
        .catch(() => {})
    }

    poll()
    if (wrapper.isConnected) {
      intervalId = setInterval(poll, this.constructor.pollIntervalMs)
      this.processingIntervals.push(intervalId)
    }
  }

  setDropzoneDisabled(disabled) {
    if (!this.hasDropzoneTarget) return
    if (disabled) {
      this.dropzoneTarget.classList.add("pointer-events-none", "opacity-50", "cursor-not-allowed")
    } else {
      this.dropzoneTarget.classList.remove("pointer-events-none", "opacity-50", "cursor-not-allowed")
    }
  }

  previewRowClassName() {
    if (this.multipleValue && this.gallerySortEnabled()) {
      return "grid grid-cols-1 sm:grid-cols-[auto_11rem_1fr] gap-4 px-4 py-4 sm:items-start"
    }

    return "grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-4 px-4 py-4 sm:items-start"
  }

  gallerySortEnabled() {
    if (!this.multipleValue || !this.hasUploadsTarget) return false

    return Boolean(this.uploadsTarget.dataset.galleryImagesSortReorderUrlValue)
  }

  createGalleryDragHandle() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className =
      "sortable-handle inline-flex size-5 shrink-0 items-center justify-center rounded-md text-gray-500 cursor-grab active:cursor-grabbing hover:text-gray-700 self-start mt-1"
    btn.setAttribute("aria-label", "Drag to reorder gallery image")
    btn.title = "Drag to reorder gallery image"

    const icon = document.createElement("i")
    icon.className = "fa-solid fa-grip-vertical text-sm leading-none"
    icon.setAttribute("aria-hidden", "true")
    btn.appendChild(icon)

    return btn
  }

  detailColumnFor(wrapper) {
    if (!wrapper) return null
    return wrapper.querySelector("[data-image-upload-detail]") || wrapper
  }

  elementToRemoveFor(wrapper) {
    if (this.multipleValue || !this.hasPreviewTarget) return wrapper

    const parent = wrapper.parentElement
    if (parent?.parentElement === this.previewTarget) {
      return parent
    }

    return wrapper
  }

  appendAltTextField(wrapper, updateUrl) {
    if (!updateUrl) return
    const col = this.detailColumnFor(wrapper)
    if (!col || col.querySelector("[data-image-alt-input]")) return

    const label = document.createElement("label")
    label.className = "text-xs font-medium text-gray-600"
    label.textContent = "Alt text (optional)"

    const row = document.createElement("div")
    row.className = "flex flex-col gap-1.5 mt-0.5"

    const input = document.createElement("input")
    input.type = "text"
    input.maxLength = 130
    input.className = "text-sm w-full px-2 py-1 border border-gray-300"
    input.dataset.updateUrl = updateUrl
    input.dataset.imageAltInput = ""

    const saveBtn = document.createElement("button")
    saveBtn.type = "button"
    saveBtn.id = `image-upload-alt-save-${Date.now()}`
    saveBtn.className =
      "expanding-button expanding-button-expanded btn-pill btn-pill-primary btn-pill-sm expanding-button-circle inline-flex items-center cursor-pointer border border-green-800 shadow-[0_2px_6px_rgba(15,23,42,0.25)] hover:shadow-[0_3px_8px_rgba(15,23,42,0.35)] self-start"
    saveBtn.setAttribute("aria-label", "Save alt text")
    saveBtn.setAttribute("data-image-alt-save", "")
    saveBtn.setAttribute("data-action", "click->image-upload#saveAltFromButton")

    const labelWrap = document.createElement("span")
    labelWrap.className = "expanding-button-label"
    labelWrap.id = `${saveBtn.id}-label`
    const labelText = document.createElement("span")
    labelText.className = "expanding-button-label-text"
    labelText.textContent = "Save"
    labelWrap.appendChild(labelText)

    const icon = document.createElement("i")
    icon.id = `${saveBtn.id}-icon`
    icon.className = "fa-solid fa-floppy-disk text-[0.8rem] leading-none"
    icon.setAttribute("aria-hidden", "true")

    saveBtn.appendChild(labelWrap)
    saveBtn.appendChild(icon)

    row.appendChild(input)
    row.appendChild(saveBtn)
    col.appendChild(label)
    col.appendChild(row)
  }

  removeFailedUploadPreviews(count) {
    if (count <= 0) return
    if (!this.multipleValue && this.hasPreviewTarget) {
      const wrappers = this.previewTarget.querySelectorAll("[data-file-id]")
      wrappers.forEach((w) => w.remove())
    } else if (this.multipleValue && this.hasUploadsTarget) {
      const wrappers = Array.from(this.uploadsTarget.querySelectorAll("[data-file-id]"))
      wrappers.slice(-count).forEach((w) => w.remove())
    }
    this.pendingFiles = this.pendingFiles.slice(0, -count)
    this.syncFilesToInput()
    this.updateMainImageDropzoneVisibility()
    this.syncHiddenByPlanNote()
    if (this.multipleValue) {
      this.dispatchGalleryCountChanged()
      this.syncGalleryTableShell()
    }
  }

  applySingleUploadResponse(data) {
    const wrapper = this.multipleValue
      ? this.uploadsTarget?.querySelector("[data-file-id]")
      : this.previewTarget?.querySelector("[data-file-id]")
    if (!wrapper) return

    const { id, signed_id, url, thumbnail_url: thumbnailUrl, blob_id: blobId, filename, byte_size: byteSize, delete_url, update_url, variant_ready_url, message } = data
    if (!id) return

    wrapper.dataset.signedId = signed_id
    wrapper.dataset.fullSrc = url
    if (id) wrapper.dataset.galleryImageId = id
    if (thumbnailUrl) wrapper.dataset.thumbnailSrc = thumbnailUrl
    if (delete_url) wrapper.dataset.deleteUrl = delete_url
    if (blobId) wrapper.dataset.blobId = blobId
    delete wrapper.dataset.fileId

    const detailCol = this.detailColumnFor(wrapper)
    if (detailCol) {
      const idEl = detailCol.querySelector('[data-role="blob-id"]')
      if (idEl && blobId) idEl.textContent = `ID: ${blobId}`

      const meta = detailCol.querySelector("p:not([data-role])")
      if (meta && filename) {
        const sizeText = byteSize ? ` · ${this.humanFileSize(byteSize)}` : ""
        meta.textContent = `${filename}${sizeText}`
      }
    }

    if (update_url) {
      this.appendAltTextField(wrapper, update_url)
    }

    if (message !== undefined) {
      this.hiddenNoteMessageValue = message
    }

    // Thing images enqueue variant processing and return variant_ready_url to poll.
    // Category (and other) uploads omit it — there is nothing to wait for; do not use the
    // 10s processingDelayMs fallback or the overlay feels "stuck".
    if (variant_ready_url) {
      this.setProcessingState(wrapper, true, variant_ready_url)
    } else {
      this.clearProcessingState(wrapper)
    }
    this.syncFilesToInput()
    this.updateMainImageDropzoneVisibility()
    this.syncHiddenByPlanNote()
    if (this.multipleValue) {
      this.dispatchGalleryCountChanged()
      this.syncGalleryTableShell()
    }
  }

  humanFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  updateMainImageDropzoneVisibility() {
    if (!this.hasDropzoneTarget || !this.hasPreviewTarget || this.multipleValue) return
    const hasImage = this.previewTarget.querySelector("[data-file-id], [data-signed-id]")
    if (hasImage) {
      this.dropzoneTarget.classList.add("hidden")
    } else {
      this.dropzoneTarget.classList.remove("hidden")
    }
    if (this.hasDefaultPreviewTarget) this.defaultPreviewTarget.hidden = Boolean(hasImage)
  }

  syncGalleryTableShell() {
    if (!this.hasGalleryTableShellTarget || !this.hasUploadsTarget) return
    const n = this.uploadsTarget.querySelectorAll("[data-file-id], [data-signed-id]").length
    this.galleryTableShellTarget.classList.toggle("hidden", n === 0)
  }

  syncHiddenByPlanNote() {
    const note = this.element.querySelector("[data-image-upload-hidden-note]")
    if (!this.hiddenNoteMessageValue) {
      note?.classList.add("hidden")
      return
    }

    if (this.uploadedImageCount() === 0) {
      note?.classList.add("hidden")
      return
    }

    if (note) {
      note.classList.remove("hidden")
      const message = note.querySelector("p")
      if (message) message.textContent = this.hiddenNoteMessageValue
      return
    }

    const heading = this.element.querySelector("h2")
    if (!heading) return

    const wrapper = document.createElement("div")
    wrapper.id = `image-upload-hidden-note-${Date.now()}`
    wrapper.className = this.multipleValue
      ? "mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
      : "mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
    wrapper.setAttribute("data-image-upload-hidden-note", "")

    const message = document.createElement("p")
    message.id = `${wrapper.id}-message`
    message.textContent = this.hiddenNoteMessageValue
    wrapper.appendChild(message)

    heading.insertAdjacentElement("afterend", wrapper)
  }

  uploadedImageCount() {
    let container = null
    if (this.multipleValue && this.hasUploadsTarget) {
      container = this.uploadsTarget
    } else if (!this.multipleValue && this.hasPreviewTarget) {
      container = this.previewTarget
    }

    return container?.querySelectorAll("[data-file-id], [data-signed-id]").length || 0
  }

}
